import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper'; // Adjust the import path as necessary
import { hexToRgb, formatNumber } from '@/utils/formatters'; // Adjust the import path as necessary
import { isOutOfBounds } from '@/features/energyxplorer/utils/timestampsSummaryHelpers';

// move to some utility function file or something
export const getNodeContent =
  (
    isDarkMode,
    showKpiGauge = false,
    showUndocumentedConsumption = false,
    timeRange,
    invalidNodeIds = [],
    errors = [],
  ) =>
  (d) => {
    const name = d.data.name || '';
    const type = d.data.type || '';
    // const nodeId = d.data.energyTreeNodeId;
    // const isInvalid = invalidNodeIds?.has(nodeId);
    // const nodeErrors = errors?.filter((e) => e.nodeId === nodeId);
    // const showErrorIcon = isInvalid && nodeErrors?.length > 0;
    const energySummary = d.data.energySummary || {};
    const totalEnergyUsage = formatNumber(
      energySummary.TotalEnergyUsage?.toFixed(0) || 0,
    );
    const notAnalyzedEnergyUsage = formatNumber(
      energySummary.NotAnalysedEnergyUsage || 0,
    );
    const notAnalyzedEnergyUsagePercentage =
      energySummary.NotAnalysedEnergyUsagePercentage || 100;

    const isResidual = d.data.options !== null;

    const isDataNode = d.data.dataStreams && d.data.dataStreams.length > 0;
    const isSEUType = (nodeType) => {
      const seuTypes = ['Hovedmåler', 'Tavle', 'Undetavle'];
      return !seuTypes.includes(nodeType);
    };

    const parentTotalEnergyUsage =
      d?.parent?.data?.energySummary?.TotalEnergyUsage ?? 0;

    const childToParentContributionPercentage =
      parentTotalEnergyUsage > 0
        ? (energySummary.TotalEnergyUsage / parentTotalEnergyUsage) * 100
        : 0;

    const typeColor = getTypeColor(type);
    const typeIcon = getTypeIcon(type);

    const contributionPercentageBorderWidth =
      childToParentContributionPercentage >= 100
        ? 100
        : childToParentContributionPercentage;

    const bgColor = isDarkMode ? '#333' : '#fff';
    const textColor = isDarkMode ? '#E4E2E9' : '#08011E';
    const borderColor = isDarkMode ? '#555' : '#E4E2E9';
    const unaccountedColor =
      notAnalyzedEnergyUsagePercentage > 5
        ? '#ffa500'
        : `rgba(${hexToRgb(textColor)}, 0.5)`;

    function trimString(str, maxLength) {
      if (str.length > maxLength) {
        return str.substring(0, maxLength) + '...';
      }
      return str; // Return the string as is if it's within the maxLength
    }

    const iconSize = 36;
    const iconOffset = -10.5;
    const scoopRadius = 19;
    const scoopCenter = 8;
    const renderWarning =
      timeRange && energySummary.TimestampsSummary
        ? isOutOfBounds(energySummary.TimestampsSummary, timeRange)
        : false;

    return `
    <div class="node-content" style="width: 300px; height: 150px; position: relative; font-family: 'Inter', sans-serif;">
     <style>
        /* Scoped-ish: these selectors only affect elements inside .node-content */
        .node-content .node-actions {
          position:absolute; top:8px; right:8px; display:flex; gap:6px;
          opacity:0; transform: translateY(-4px);
          transition: opacity .15s ease, transform .15s ease;
          pointer-events: none; /* prevent stealing hover until visible */
          z-index: 3;
        }
        .node-content:hover .node-actions {
          opacity:1; transform: translateY(0); pointer-events: auto;
        }
        .node-content .node-action {
          width:28px; height:28px; border-radius:6px;
          display:inline-flex; align-items:center; justify-content:center;
          background:${isDarkMode ? '#2c2c2c' : '#fff'};
          border:1px solid ${isDarkMode ? '#555' : '#E4E2E9'};
          color: ${isDarkMode ? '#E4E2E9' : '#716E7B'};
          cursor:pointer;
        }
        .node-content .node-action i { font-size:12px; }
        .node-content .node-action:hover {
          box-shadow: 0 0 0 1px ${isDarkMode ? '#444' : '#e8e8e8'} inset;
        }
        .node-content .node-action.danger {
         border-color: ${isDarkMode ? '#7a2c2c' : '#ffcccc'};
         background: ${isDarkMode ? '#3a1f1f' : '#fff6f6'};
       }
       .node-content .node-action.danger:hover {
         box-shadow: 0 0 0 1px ${isDarkMode ? '#5c2a2a' : '#ffe3e3'} inset;
       }
      </style>
      
      <svg width="100%" height="100%" viewBox="0 0 300 150"
        style="position:absolute; top:0; left:0; z-index:0;"
        xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask id="scoopMask">
            <rect width="100%" height="100%" fill="white" />
            <circle cx="${scoopCenter}" cy="${scoopCenter}" r="${scoopRadius}" fill="none" />
          </mask>
        </defs>

        <rect width="100%" height="100%" fill="${bgColor}" mask="url(#scoopMask)" rx="16" ry="16"/>
        <rect x="0.5" y="0.5" width="299" height="149" rx="15.5" ry="15.5"
          fill="none" stroke="${
            isDarkMode ? '#555' : '#E4E2E9'
          }" stroke-width="${isResidual ? '4' : '1'}" mask="url(#scoopMask)"
          shape-rendering="geometricPrecision" stroke-dasharray="${isResidual ? '4' : 'none'}"/>
        <path 
          d="
            M 25.8 0 
            A 19.5 19.5 0 0 1 0 25.8
            L -100 0
          "
          fill="${isDarkMode ? '#212121' : '#fff'}" 
          stroke="${isDarkMode ? '#555' : '#E4E2E9'}" 
          stroke-width="${isResidual ? '2' : '1'}"
          shape-rendering="geometricPrecision" 
          stroke-dasharray="${isResidual ? '4' : 'none'}"
        />
      </svg>

        <div class="node-actions" data-nodeid="${d.data.energyTreeNodeId}">
        <div class="node-action btn-add" title="Tilføj ny under" role="button" aria-label="Add child">
          <i class="fa-solid fa-plus"></i>
        </div>
        <div class="node-action btn-edit" title="Rediger" role="button" aria-label="Edit node">
          <i class="fa-regular fa-pen-to-square"></i>
        </div>
        <div class="node-action btn-move" title="Flyt" role="button" aria-label="Move node">
          <i class="fa-solid fa-arrows-turn-to-dots"></i>
        </div>
        <div class="node-action btn-delete danger" title="Slet" role="button" aria-label="Delete node">
          <i class="fa-regular fa-trash-can"></i>
        </div>
      </div>


      <div style="
        position: absolute;
        top: ${iconOffset}px;
        left: ${iconOffset}px;
        width: ${iconSize}px;
        height: ${iconSize}px;
        background-color: ${typeColor};
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        z-index: 2;
      ">
         <div 
          style="cursor: pointer;"
          onmouseover="this.querySelector('.tooltip').style.opacity='1'; this.querySelector('.tooltip').style.visibility='visible';"
          onmouseout="this.querySelector('.tooltip').style.opacity='0'; this.querySelector('.tooltip').style.visibility='hidden';"
        >
        <i class="fa-solid ${typeIcon}"></i>
         ${
           renderWarning
             ? `
        <i class="fa-solid fa-triangle-exclamation" style="position: absolute; top: -5px; right: -3px; color: ${isDarkMode ? '#ff9800' : '#ff9800'}; font-size: 14px;"></i>
          `
             : ''
         }
          <div class="tooltip" style="visibility: hidden; opacity: 0; transition: opacity 0.3s; position: absolute; bottom: 30%; left: 50%; transform: translateX(-110%); background: ${isDarkMode ? 'white' : 'black'}; color: ${isDarkMode ? 'black' : 'white'}; padding: 4px 8px; border-radius: 4px; font-size: 12px; white-space: nowrap;">
              Der mangler data i den valgte periode <br>juster tidsintervallet via datovælgeren.
            </div>
         </div>
      </div>
      

      <div style="
        position: relative;
        z-index: 2;
        color: ${textColor};
        padding: 16px;
        padding-top: ${iconSize + Math.abs(iconOffset)}px;
      ">
         <div style="
          font-size: 14px;
          max-width: 200px;        /* set a limit */
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        ">
          ${name}
        </div>
        <div style="display: flex; flex-direction: row; align-items: center; font-size: 14px;">
        

          <div>
          ${
            !showUndocumentedConsumption
              ? `
            <div style="font-weight: bold; font-size: 28px;">${totalEnergyUsage} kWh</div>
          `
              : `
            <div style="font-weight: bold; font-size: 28px;">${notAnalyzedEnergyUsage}</div>
          `
          }
          </div>

           ${
             isDataNode
               ? `
                <i class="fa-solid fa-wifi" style="transform: rotate(45deg); margin-left: 4px;"></i>
              `
               : ''
           }

          ${
            showKpiGauge
              ? `
            <div style="transition: all 0.3s ease;">
              <div style="position: relative; width: 50px; height: 50px; margin-top: -17px; margin-right: 10px;">
                <div style="
                  position: absolute;
                  width: 60px;
                  height: 60px;
                  border-radius: 50%;
                  background: conic-gradient(
                    ${showUndocumentedConsumption ? '#f44336' : '#4caf50'} ${
                      showUndocumentedConsumption
                        ? notAnalyzedEnergyUsagePercentage
                        : childToParentContributionPercentage
                    }%,
                    ${isDarkMode ? '#6e6e6e' : '#e0e0e0'} 0%
                  );
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  color: ${isDarkMode ? 'white' : 'black'};
                  font-size: 12px;
                  font-weight: bold;
                ">
                  <div style="position: absolute; inset: 8px; background: ${bgColor}; border-radius: 50%; z-index: 1;"></div>
                  <div style="position: relative; z-index: 2;">
                    ${
                      showUndocumentedConsumption
                        ? `${notAnalyzedEnergyUsagePercentage.toFixed(0)}%`
                        : `${childToParentContributionPercentage.toFixed(0)}%`
                    }
                  </div>
                </div>
              </div>
            </div>
          `
              : ''
          }
         

        </div>
        </div>
        ${
          energySummary?.AnalysedEnergyUsage &&
          energySummary?.AnalysedEnergyUsagePercentage
            ? `
              <div style="font-size: 14px; position: absolute; bottom: 40px; left: 16px; color: ${isDarkMode ? 'white' : 'black'};">
                Målt forbrug: ${formatNumber(energySummary?.AnalysedEnergyUsage)} kWh (${energySummary?.AnalysedEnergyUsagePercentage?.toFixed(0)}%)
              </div>
            `
            : ''
        }
      <div style="
            position: relative;
            width: 100%;
            bottom: -23px;
            height: 15px;
            background-color: ${borderColor};
            background-color: rgba(${hexToRgb(typeColor)}, 0.3);
            border-bottom-right-radius:25px;
            border-bottom-left-radius:25px;
            z-index: 1;
          ">
            <div style="
              position: absolute;
              height: 100%;
              width: ${
                contributionPercentageBorderWidth > 1
                  ? contributionPercentageBorderWidth
                  : 0
              }%;
              background-color: ${typeColor};
              transition: width 0.4s ease;
              border-bottom-left-radius:15px;
              border-bottom-right-radius: ${
                contributionPercentageBorderWidth >= 100 ? 10 : 0
              }px;
            "></div>
          </div>
    </div>
  `;
  };

export const getButtonContent =
  (isDarkMode) =>
  ({ node }) => {
    const textColor = isDarkMode ? '#E4E2E9' : '#716E7B';
    const bgColor = isDarkMode ? '#333' : 'white';
    const borderColor = isDarkMode ? '#555' : '#E4E2E9';

    return `
    <div style="
      color: ${textColor};
      border-radius: 6px;
      padding: 4px;
      font-size: 10px;
      margin: auto auto;
      background-color: ${bgColor};
      border: 1px solid ${borderColor};
    ">
      <span style="font-size: 9px">
        ${
          node.children
            ? `<i class="fas fa-angle-up"></i>`
            : `<i class="fas fa-angle-down"></i>`
        }
      </span> ${node.data._directSubordinates}
    </div>
  `;
  };
